import { isStageAvailable } from '@protoss/shared';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { AppShell, MainContentShell } from '../components/shell';
import { useLayout, useLayoutConfig } from '../contexts/LayoutContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { AiCreditsReport } from '../features/ai-credits/AiCreditsReport';
import { useWorkspaceMenu } from '../features/workspace-menu/hooks/useWorkspaceMenu';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import { AdminContextMenuPane } from './AdminContextMenuPane';
import './administration-page.css';
import { IntegrationsPage } from './IntegrationsPage';
import { WorkspaceProductsPage } from './WorkspaceProductsPage';
import { WorkspaceSettingsPage } from './WorkspaceSettingsPage';
import { WorkspaceUsersPage } from './WorkspaceUsersPage';

/**
 * AdministrationPageContent - administration interface with AppShell
 *
 * Default behavior:
 * - LeftNavPane visible
 * - ContextPane visible (admin menu)
 * - RightUtilityPane hidden
 * - BottomPane hidden
 */
function AdministrationPageContent() {
  const navigate = useNavigate();
  const { section } = useParams<{ section?: string }>();
  const { workspace, isLoadingWorkspace, modules } = useWorkspace();
  const hasSalesModule = modules.some((m) => m.key === 'sales');
  const { contextPaneWidth, setContextPaneWidth } = useLayout();
  const [adminMenuCollapsed, setAdminMenuCollapsed] = React.useState(false);
  const savedPaneWidth = React.useRef(contextPaneWidth);
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);
  const { items: menuItems, isLoading: isMenuLoading } = useWorkspaceMenu();

  // Declarative base layout: context pane (admin menu) shown, reset on unmount.
  useLayoutConfig({ showContextPane: true });

  // Guard: restore if context pane was left at a collapsed width by a previous
  // page or session. useLayoutEffect so the menu never paints clipped in a
  // too-narrow pane for a frame.
  React.useLayoutEffect(() => {
    if (contextPaneWidth < 100) {
      setContextPaneWidth(280);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleAdminMenu = React.useCallback(() => {
    if (adminMenuCollapsed) {
      setContextPaneWidth(savedPaneWidth.current);
      setAdminMenuCollapsed(false);
    } else {
      savedPaneWidth.current = contextPaneWidth;
      // Collapse to same width as left nav so icons align visually.
      const navCollapsedWidth = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--pane-leftnav-width-collapsed'
        ),
        10
      );
      setContextPaneWidth(navCollapsedWidth);
      setAdminMenuCollapsed(true);
    }
  }, [adminMenuCollapsed, contextPaneWidth, setContextPaneWidth]);

  // Redirect non-admin users
  React.useEffect(() => {
    if (!isLoadingWorkspace && !isWorkspaceAdmin) {
      navigate('/');
    }
  }, [isLoadingWorkspace, isWorkspaceAdmin, navigate]);

  // When no section in URL, navigate to the first enabled menu item
  React.useEffect(() => {
    if (!isMenuLoading && !section && menuItems.length > 0) {
      const firstEnabled = menuItems.find((item) => item.isEnabled);
      if (firstEnabled) {
        navigate(`/administration/${firstEnabled.routeSegment}`, { replace: true });
      }
    }
  }, [menuItems, isMenuLoading, section, navigate]);

  // The active section is driven by the URL param
  const selectedAdminMenu = section ?? '';

  // LeftNavPane content - DB-driven via AppNav
  const leftNav = (
    <AppNav
      selected="administration"
      hidePrimary
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => {}}
    />
  );

  // ContextPane content - DB-driven admin menu
  const contextPane = (
    <AdminContextMenuPane
      selectedSection={selectedAdminMenu}
      collapsed={adminMenuCollapsed}
      onToggle={handleToggleAdminMenu}
      menuItems={menuItems}
      isMenuLoading={isMenuLoading}
    />
  );

  // MainPane content - renders the sub-page for the active routeSegment
  const activeItem = menuItems.find((item) => item.routeSegment === selectedAdminMenu);
  const pageTitle = activeItem?.label ?? 'Administration';

  const main = (
    <MainContentShell title={pageTitle}>
      {selectedAdminMenu === 'workspace' && <WorkspaceSettingsPage />}
      {selectedAdminMenu === 'integrations' && (
        <IntegrationsPage isWorkspaceAdmin={isWorkspaceAdmin} />
      )}
      {selectedAdminMenu === 'products' && <WorkspaceProductsPage />}
      {selectedAdminMenu === 'users' && <WorkspaceUsersPage />}
      {selectedAdminMenu === 'ai-credits' &&
        isStageAvailable('alpha', workspace?.featureMinStage) && (
          <AiCreditsReport endpoint="/api/workspace/admin/ai-credits" showUserBreakdown />
        )}
    </MainContentShell>
  );

  return (
    <AppShell
      headerShowBadge={hasSalesModule}
      leftNav={leftNav}
      contextPane={contextPane}
      contextPaneDividerClassName="admin-contextpane-divider"
      contextPaneCollapsed={adminMenuCollapsed}
      main={main}
    />
  );
}

/**
 * AdministrationPage - administration interface
 *
 * Layout structure (frontend-architecture.md §7):
 *   LeftNavPane | ContextPane | MainPane | RightUtilityPane
 *
 * This page is only accessible to workspace administrators.
 */
export function AdministrationPage() {
  return <AdministrationPageContent />;
}
